if (!customElements.get('alisa-product-gallery')) {
  customElements.define('alisa-product-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.track = this.querySelector('[data-dhg-track]');
      this.slides = Array.from(this.querySelectorAll('[data-dhg-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-dhg-thumb]'));
      this.dialog = this.querySelector('[data-dhg-dialog]');
      this.zoomImage = this.querySelector('[data-dhg-dialog-image]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      if (!this.track || !this.slides.length) return;
      this.index = 0;
      this.querySelector('[data-dhg-zoom]').hidden = false;
      this.querySelector('[data-dhg-controls]').hidden = this.slides.length < 2;
      this.thumbs.forEach((button, index) => button.addEventListener('click', () => this.go(index, true), { signal }));
      this.querySelector('[data-dhg-prev]').addEventListener('click', () => this.go(this.index - 1, true), { signal });
      this.querySelector('[data-dhg-next]').addEventListener('click', () => this.go(this.index + 1, true), { signal });
      this.querySelector('[data-dhg-zoom]').addEventListener('click', event => this.open(event.currentTarget), { signal });
      this.slides.forEach((slide, index) => slide.querySelector('[data-dhg-open]').addEventListener('click', event => {
        if (Date.now() - (this.draggedAt || 0) < 250) return;
        this.select(index, false);
        this.open(event.currentTarget);
      }, { signal }));
      this.track.addEventListener('pointerdown', event => { this.pointer = { x: event.clientX, y: event.clientY }; }, { signal, passive: true });
      this.track.addEventListener('pointerup', event => {
        if (this.pointer && Math.hypot(event.clientX - this.pointer.x, event.clientY - this.pointer.y) > 10) this.draggedAt = Date.now();
        this.pointer = null;
      }, { signal, passive: true });
      this.track.addEventListener('pointercancel', () => { this.draggedAt = Date.now(); this.pointer = null; }, { signal, passive: true });
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => {
          const bounds = this.track.getBoundingClientRect();
          let closest = 0;
          let distance = Infinity;
          this.slides.forEach((slide, index) => {
            const delta = Math.abs(slide.getBoundingClientRect().left - bounds.left);
            if (delta < distance) { closest = index; distance = delta; }
          });
          this.select(closest, false);
        });
      }, { signal, passive: true });
      this.track.addEventListener('keydown', event => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault(); this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1), true);
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault(); this.go(event.key === 'Home' ? 0 : this.slides.length - 1, true);
        }
      }, { signal });
      this.querySelector('[data-dhg-thumbs]').addEventListener('keydown', event => {
        const current = this.thumbs.indexOf(event.target);
        if (current < 0 || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let next = current + (event.key === 'ArrowRight' ? 1 : -1);
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.thumbs.length - 1;
        next = Math.max(0, Math.min(this.thumbs.length - 1, next));
        this.go(next, true); this.thumbs[next].focus({ preventScroll: true });
      }, { signal });
      this.querySelector('[data-dhg-close]').addEventListener('click', () => this.dialog.close(), { signal });
      this.querySelector('[data-dhg-detail]').addEventListener('click', () => this.detail(), { signal });
      this.zoomImage.addEventListener('click', () => this.detail(), { signal });
      this.querySelector('[data-dhg-dialog-prev]').addEventListener('click', () => this.go(this.index - 1, true), { signal });
      this.querySelector('[data-dhg-dialog-next]').addEventListener('click', () => this.go(this.index + 1, true), { signal });
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, { signal });
      this.dialog.addEventListener('keydown', event => {
        if (this.dialog.hasAttribute('data-detail')) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault(); event.stopPropagation(); this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1), true);
        }
      }, { signal });
      this.dialog.addEventListener('close', () => {
        this.detail(false);
        if (this.previousOverflow !== undefined) document.body.style.overflow = this.previousOverflow;
        this.previousOverflow = undefined;
        this.returnFocus?.focus({ preventScroll: true });
      }, { signal });
      this.addEventListener('shopify:block:select', event => {
        const target = event.target.closest('[data-dhg-slide]');
        const index = this.slides.indexOf(target);
        if (index >= 0) this.go(index, false);
      }, { signal });
      this.resize = new ResizeObserver(() => {
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => this.go(this.index, false, true));
      });
      this.resize.observe(this.track);
      this.select(0, false);
    }
    go(index, announce, instant = false) {
      index = Math.max(0, Math.min(this.slides.length - 1, index));
      const trackRect = this.track.getBoundingClientRect();
      const left = this.slides[index].getBoundingClientRect().left - trackRect.left + this.track.scrollLeft;
      this.track.scrollTo({ left, behavior: instant || this.motion.matches || this.dialog.open ? 'instant' : 'smooth' });
      this.select(index, announce);
      if (this.dialog.open) this.renderZoom();
    }
    select(index, announce) {
      this.index = index;
      this.thumbs.forEach((thumb, current) => thumb.setAttribute('aria-current', String(current === index)));
      this.slides.forEach((slide, current) => { slide.querySelector('[data-dhg-open]').tabIndex = current === index ? 0 : -1; });
      this.querySelector('[data-dhg-current]').textContent = String(index + 1).padStart(2, '0');
      const label = this.slides[index].dataset.dhgLabel || this.slides[index].querySelector('img').alt;
      this.querySelector('[data-dhg-caption]').textContent = label;
      this.querySelector('[data-dhg-prev]').disabled = index === 0;
      this.querySelector('[data-dhg-next]').disabled = index === this.slides.length - 1;
      const rail = this.querySelector('[data-dhg-thumbs]');
      const thumb = this.thumbs[index];
      if (thumb) {
        const bounds = rail.getBoundingClientRect(), item = thumb.getBoundingClientRect();
        if (item.left < bounds.left || item.right > bounds.right) rail.scrollTo({ left: rail.scrollLeft + item.left - bounds.left - (bounds.width - item.width) / 2, behavior: this.motion.matches ? 'instant' : 'smooth' });
      }
      if (announce) this.querySelector('[data-dhg-status]').textContent = `Imagen ${index + 1} de ${this.slides.length}: ${label}`;
    }
    open(trigger) {
      this.returnFocus = trigger;
      this.renderZoom();
      if (!this.dialog.open) {
        this.previousOverflow = document.body.style.overflow;
        this.dialog.showModal();
        document.body.style.overflow = 'hidden';
      }
    }
    renderZoom() {
      const slide = this.slides[this.index];
      const source = slide.querySelector('[data-dhg-open]');
      const alt = slide.querySelector('img').alt;
      this.zoomImage.src = source.dataset.dhgSrc;
      this.zoomImage.alt = alt;
      this.zoomImage.hidden = false;
      this.querySelector('[data-dhg-dialog-caption]').textContent = slide.dataset.dhgLabel || alt;
      this.querySelector('[data-dhg-dialog-count]').textContent = `${this.index + 1} / ${this.slides.length}`;
      this.querySelector('[data-dhg-dialog-prev]').disabled = this.index === 0;
      this.querySelector('[data-dhg-dialog-next]').disabled = this.index === this.slides.length - 1;
      this.detail(false);
    }
    detail(value = !this.dialog.hasAttribute('data-detail')) {
      this.dialog.toggleAttribute('data-detail', value);
      const button = this.querySelector('[data-dhg-detail]');
      button.setAttribute('aria-pressed', String(value));
      button.setAttribute('aria-label', value ? 'Ver imagen completa' : 'Ver detalle de la imagen');
      button.textContent = value ? '−' : '+';
      const viewport = this.querySelector('[data-dhg-dialog-viewport]');
      viewport.scrollTo({ left: 0, top: 0, behavior: 'instant' });
    }
    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      if (this.previousOverflow !== undefined) document.body.style.overflow = this.previousOverflow;
      this.controller?.abort();
      this.resize?.disconnect();
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.resizeFrame);
      this.controller = null;
    }
  });
}
